import {useEffect, useMemo, useState} from 'react'
import {format} from 'date-fns'
import {DatePicker} from '@material-ui/pickers'
import {useTranslation} from 'react-i18next'

import ImageCalendarDay from '../Images/ImageCalendarDay'
import {
  buildPlanetHdCalendarModel,
  getPlanetHdDayCloudState,
  hasKnownCloudPercent,
  planetHdItemKey,
} from '../Images/planetHdCalendarModel'
import {REMOTE_SENSING_DATE_STATES} from '../Images/remoteSensingDateModel'

const displayDate = (date) => format(new Date(`${date}T00:00:00`), 'dd/MM/yyyy')

const ComparisonHdDatePicker = ({
  autoOpenSignal = 0,
  cloudinessFilter,
  disabled,
  items,
  label,
  onChange,
  placeholder,
  value,
}) => {
  const {t} = useTranslation()
  const [open, setOpen] = useState(false)
  const [inspectedDate, setInspectedDate] = useState('')
  const {itemsByDate, availableDates} = useMemo(
    () => buildPlanetHdCalendarModel(items, cloudinessFilter),
    [items, cloudinessFilter]
  )
  const selectedItem = items.find((item) => planetHdItemKey(item) === value)
  const selectedDate = selectedItem?.date || (itemsByDate.has(inspectedDate) ? inspectedDate : '')
  const pickerDate = selectedDate || availableDates[0]
  const isDisabled = disabled || availableDates.length === 0

  useEffect(() => {
    if (autoOpenSignal && !isDisabled) setOpen(true)
  }, [autoOpenSignal, isDisabled])

  const cloudLabel = (item) => hasKnownCloudPercent(item)
    ? t('images:hd_dates.cloud_percent', {
      percent: item.cloudPercent.toFixed(1),
      defaultValue: `${item.cloudPercent.toFixed(1)}% nubes`,
    })
    : t('images:hd_dates.unknown_cloud', {defaultValue: 'Nubosidad desconocida'})

  const renderCalendarDay = (day, pickerSelectedDate, inCurrentMonth, dayComponent) => {
    if (!inCurrentMonth) return dayComponent
    const date = format(day, 'yyyy-MM-dd')
    const bucket = itemsByDate.get(date) || []
    const {cloudState, representative} = getPlanetHdDayCloudState(bucket, cloudinessFilter)
    const description = representative ? cloudLabel(representative) :
      t('images:hd_dates.no_image', {defaultValue: 'Sin imagen'})

    return <ImageCalendarDay
      inCurrentMonth={inCurrentMonth}
      cloudState={representative ? cloudState : null}
      active={(selectedDate || (pickerSelectedDate && format(pickerSelectedDate, 'yyyy-MM-dd'))) === date}
      title={description}
      ariaLabel={`${displayDate(date)} · ${description}`}
    >
      {dayComponent}
    </ImageCalendarDay>
  }

  // Like Capas, every catalogued date remains inspectable. Failed scenes do
  // not render a comparison, but selecting them explains that the image is
  // unavailable instead of making a colored calendar day mysteriously inert.
  const shouldDisableDate = (date) => !itemsByDate.has(format(date, 'yyyy-MM-dd'))

  const chooseDay = (date) => {
    const dateValue = format(date, 'yyyy-MM-dd')
    const bucket = itemsByDate.get(dateValue) || []
    // Color and click must use the same representative. Filtering by status
    // here used to make a green day select a different, cloudier scene.
    const {representative} = getPlanetHdDayCloudState(bucket, cloudinessFilter)
    if (representative) {
      setInspectedDate(dateValue)
      onChange(planetHdItemKey(representative))
    }
    setOpen(false)
  }

  return (
    <div className={`view-land-comparison-field view-land-comparison-date ${open ? 'open' : ''}`.trim()}>
      <span className='view-land-comparison-field__label'>{label}</span>
      <button
        type='button'
        className='view-land-comparison-field__control view-land-comparison-date__button'
        aria-expanded={open}
        disabled={isDisabled}
        onClick={() => setOpen(true)}
      >
        <span className={`view-land-comparison-select__value ${selectedDate ? '' : 'is-placeholder'}`.trim()}>
          {selectedDate ? displayDate(selectedDate) : placeholder}
        </span>
        <span className='view-land-comparison-select__chevron' />
      </button>
      <DatePicker
        open={open}
        value={pickerDate ? new Date(`${pickerDate}T00:00:00`) : new Date()}
        autoOk
        okLabel={null}
        cancelLabel={t('common:actions.cancel')}
        renderDay={renderCalendarDay}
        shouldDisableDate={shouldDisableDate}
        onChange={chooseDay}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        animateYearScrolling
        DialogProps={{maxWidth: false, PaperProps: {className: 'view-land-comparison-date-dialog'}}}
        TextFieldComponent={() => null}
      />
      {selectedItem && [REMOTE_SENSING_DATE_STATES.REJECTED, REMOTE_SENSING_DATE_STATES.FAILED]
        .includes(selectedItem.status) && (
        <small className='view-land-comparison-date__message' role='alert'>
          {t('images:hd_dates.image_unavailable', {
            defaultValue: 'No se pudo cargar esta imagen. Elegí otra fecha.',
          })}
        </small>
      )}
    </div>
  )
}

export default ComparisonHdDatePicker
